import React, { useState, useCallback, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { PaymentType, ProposalSaleType, ProposalStatus, VOApplicationStageLabels } from '@trnsact/trnsact-shared-types';

interface ProposalsFiltersProps {
  filters: Record<string, any>;
  onFilterChange: (filterKey: string, filterValue: any) => void;
  onClearFilters: () => void;
  disabled?: boolean;
}

export const ProposalsFilters: React.FC<ProposalsFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Local state for text inputs to provide immediate feedback
  const [localTextValues, setLocalTextValues] = useState({
    businessName: '',
    fullName: '',
    salesPersonName: '',
    financeManagerFullName: '',
    locationName: '',
    amountRequestedMin: '',
    amountRequestedMax: '',
  });

  // Sync local state with filters prop when filters change externally (e.g., clear all)
  useEffect(() => {
    setLocalTextValues({
      businessName: filters.businessName || '',
      fullName: filters.fullName || '',
      salesPersonName: filters.salesPersonName || '',
      financeManagerFullName: filters.financeManagerFullName || '',
      locationName: filters.locationName || '',
      amountRequestedMin: filters.amountRequestedMin || '',
      amountRequestedMax: filters.amountRequestedMax || '',
    });
  }, [
    filters.businessName,
    filters.fullName,
    filters.salesPersonName,
    filters.financeManagerFullName,
    filters.locationName,
    filters.amountRequestedMin,
    filters.amountRequestedMax,
  ]);

  // Create debounced filter change handlers
  const debouncedFilterChange = useMemo(
    () =>
      debounce((filterKey: string, filterValue: any) => {
        onFilterChange(filterKey, filterValue);
      }, 500),
    [onFilterChange]
  );

  const handleTextInputChange = useCallback(
    (filterKey: string, value: string) => {
      // Update local state immediately for responsive UI
      setLocalTextValues(prev => ({ ...prev, [filterKey]: value }));
      // Call debounced filter change
      debouncedFilterChange(filterKey, value || null);
    },
    [debouncedFilterChange]
  );

  const handleNumberInputChange = useCallback(
    (filterKey: string, value: string) => {
      // Update local state immediately for responsive UI
      setLocalTextValues(prev => ({ ...prev, [filterKey]: value }));
      // Call debounced filter change with number or null
      debouncedFilterChange(filterKey, value ? Number(value) : null);
    },
    [debouncedFilterChange]
  );

  // Cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [debouncedFilterChange]);

  const activeFiltersCount = Object.keys(filters).filter(key => {
    const value = filters[key];
    return value !== null && value !== undefined && value !== '';
  }).length;

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterListIcon />
          <Typography>
            Advanced Filters
            {activeFiltersCount > 0 && (
              <Typography component="span" color="primary" sx={{ ml: 1, fontWeight: 'bold' }}>
                ({activeFiltersCount} active)
              </Typography>
            )}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Row 1: Customer & Contact */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Customer Name"
              value={localTextValues.businessName}
              onChange={e => handleTextInputChange('businessName', e.target.value)}
              placeholder="Search by business name"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Contact Name"
              value={localTextValues.fullName}
              onChange={e => handleTextInputChange('fullName', e.target.value)}
              placeholder="Search by contact name"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Sales Rep"
              value={localTextValues.salesPersonName}
              onChange={e => handleTextInputChange('salesPersonName', e.target.value)}
              placeholder="Search by sales rep"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Finance Manager"
              value={localTextValues.financeManagerFullName}
              onChange={e => handleTextInputChange('financeManagerFullName', e.target.value)}
              placeholder="Search by finance manager"
              disabled={disabled}
            />
          </Grid>

          {/* Row 2: Status & Type */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" disabled={disabled}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={e => onFilterChange('status', e.target.value || null)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={ProposalStatus.ProposalSent}>Proposal Sent</MenuItem>
                <MenuItem value={ProposalStatus.Accepted}>Accepted</MenuItem>
                <MenuItem value={ProposalStatus.ViewedByCustomer}>Viewed By Customer</MenuItem>
                <MenuItem value={ProposalStatus.Draft}>Draft</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" disabled={disabled}>
              <InputLabel>Sale Type</InputLabel>
              <Select
                value={filters.saleType || ''}
                label="Sale Type"
                onChange={e => onFilterChange('saleType', e.target.value || null)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={ProposalSaleType.FinanceOnly}>Finance Only</MenuItem>
                <MenuItem value={ProposalSaleType.FinanceWithAftermarketMenu}>Finance with Aftermarket</MenuItem>
                <MenuItem value={ProposalSaleType.CashWithAftermarketMenu}>Cash with Aftermarket</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" disabled={disabled}>
              <InputLabel>Equipment Condition</InputLabel>
              <Select
                value={filters.newUsed || ''}
                label="Equipment Condition"
                onChange={e => onFilterChange('newUsed', e.target.value || null)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="used">Used</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" disabled={disabled}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={filters.paymentType || ''}
                label="Payment Method"
                onChange={e => onFilterChange('paymentType', e.target.value || null)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={PaymentType.Cash}>Cash</MenuItem>
                <MenuItem value={PaymentType.CreditLine}>Credit Line</MenuItem>
                <MenuItem value={PaymentType.Financing}>Financing</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Row 3: Location & Stage */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              value={localTextValues.locationName}
              onChange={e => handleTextInputChange('locationName', e.target.value)}
              placeholder="Search by location"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" disabled={disabled}>
              <InputLabel>Opportunity Stage</InputLabel>
              <Select
                value={filters.applicationStage || ''}
                label="Opportunity Stage"
                onChange={e => onFilterChange('applicationStage', e.target.value || null)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={VOApplicationStageLabels.prospect}>Prospect</MenuItem>
                <MenuItem value={VOApplicationStageLabels.pre_qualified}>Pre-Qualified</MenuItem>
                <MenuItem value={VOApplicationStageLabels.approved}>Approved</MenuItem>
                <MenuItem value={VOApplicationStageLabels.funded}>Funded</MenuItem>
                <MenuItem value={VOApplicationStageLabels.cancelled}>Cancelled</MenuItem>
                <MenuItem value={VOApplicationStageLabels.declined}>Declined</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Min Amount"
              value={localTextValues.amountRequestedMin}
              onChange={e => handleNumberInputChange('amountRequestedMin', e.target.value)}
              placeholder="Min amount"
              InputProps={{ inputProps: { min: 0 } }}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max Amount"
              value={localTextValues.amountRequestedMax}
              onChange={e => handleNumberInputChange('amountRequestedMax', e.target.value)}
              placeholder="Max amount"
              InputProps={{ inputProps: { min: 0 } }}
              disabled={disabled}
            />
          </Grid>

          {/* Row 4: Date Range */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created From"
              value={filters.from || ''}
              onChange={e => onFilterChange('from', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Created To"
              value={filters.to || ''}
              onChange={e => onFilterChange('to', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
            />
          </Grid>

          {/* Clear Filters Button */}
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" height="100%">
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClearFilters}
                disabled={disabled || activeFiltersCount === 0}
              >
                Clear All Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
